import React from 'react';
import styled from '@emotion/styled';
import { Checkbox, ITheme, RadioButton } from '../../../../../../ui';
import { useExtrasFormContext } from '../helpers/useExtrasFormContext';
import { IOption } from './types';
import { getNestedInputName } from '../nested-input/getNestedInputName';
import { IPrice } from '../../../../../shared/i18n/price/IPrice';
import { getFormattedPrice } from '../../../../../shared/i18n/price/utils/getFormattedPrice';
import themed from '../../../../../../ui/themer/themed';
import { IExtrasFormData } from '../IExtrasFormData';
import { Path } from 'react-hook-form';

export interface IOptionProps<TOptionId> {
	option: IOption<TOptionId>;
	className?: string;
	type: 'radio' | 'checkbox';
}

export const Option = <TOptionId extends string | number>({
	option,
	type,
	className,
}: IOptionProps<TOptionId>) => {
	const { register } = useExtrasFormContext();
	const { label, nestedInputLevel, price } = option;

	const Selector = type === 'checkbox' ? Checkbox : RadioButton;

	const name = getNestedInputName(
		nestedInputLevel,
		'selected',
	) as Path<IExtrasFormData>;

	return (
		<OptionWrap className={className}>
			<Selector
				value={option.id}
				label={<OptionLabel name={label} price={price} />}
				{...register(name)}
			/>
		</OptionWrap>
	);
};

export const OptionWrap = styled.div`
	padding: 0;
`;

interface IOptionLabelProps {
	name: string;
	price?: IPrice;
}

export const OptionLabel = ({ name, price }: IOptionLabelProps) => (
	<>
		<StyledLabelName>{name}</StyledLabelName>
		{price && <StyledPrice>{getFormattedPrice(price)}</StyledPrice>}
	</>
);

const StyledLabelName = styled.span`
	margin-right: 20px;
`;

const StyledPrice = styled.span<{}, ITheme>`
	color: ${themed(({ color }) => color.highlight)};
	font-weight: 700;
`;
